import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  TrashIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  ExclamationCircleIcon,
  ChevronDownIcon,
  LockClosedIcon,
  MegaphoneIcon,
  XMarkIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import {
  subscribeToClassChat,
  sendClassChatMessage,
  deleteClassChatMessage,
  clearClassChat,
} from '../../services/firebaseService';
import { extractYouTubeVideoId } from '../../utils/helpers';
import { ClassChatMessage } from '../../types';

export interface ClassChatBoxProps {
  currentUser: {
    name: string;
    id: string;
    role: 'student' | 'instructor' | 'admin';
    badge?: string;
  };
  isInstructor?: boolean;
  theme?: 'dark-blue' | 'slate';
  className?: string;
  maxHeightClass?: string;
  /** Live viewer count to display in header */
  viewerCount?: number;
  /** YouTube stream URL or live video URL */
  youtubeUrl?: string;
}

const SEND_DEBOUNCE_MS = 1200;

interface ReplyTarget {
  studentId: string;
  studentName: string;
  messageId?: string;
  snippet: string;
}

const ClassChatBox: React.FC<ClassChatBoxProps> = ({
  currentUser,
  isInstructor = false,
  theme = 'dark-blue',
  className = '',
  maxHeightClass = 'h-[480px] sm:h-[540px]',
  viewerCount,
  youtubeUrl,
}) => {
  const [messages, setMessages] = useState<ClassChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [errorTimer, setErrorTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [lastSentTime, setLastSentTime] = useState<number>(0);
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isAtBottom, setIsAtBottom] = useState(true);

  // Instructor 1-to-1 Private Reply state
  const [replyTarget, setReplyTarget] = useState<ReplyTarget | null>(null);
  const [instructorFilter, setInstructorFilter] = useState<'all' | 'private_only' | 'students_only'>('all');

  const isPrivileged = isInstructor || currentUser.role === 'instructor' || currentUser.role === 'admin';
  const ytVideoId = extractYouTubeVideoId(youtubeUrl);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const prevMessageCountRef = useRef(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Real-time chat subscription isolated to this specific YouTube video link
  useEffect(() => {
    const unsubscribe = subscribeToClassChat(
      (newMessages) => {
        setMessages(newMessages);
      },
      60,
      youtubeUrl
    );
    return () => unsubscribe();
  }, [youtubeUrl]);

  // Privacy Filtering Logic:
  // - Students only see:
  //   1) Their own messages
  //   2) Public announcements/answers from the instructor (broadcast to all)
  //   3) Private 1-to-1 replies sent directly to THEM by the instructor
  // - Instructors see the global chat (all student queries + all replies)
  const visibleMessages = useMemo(() => {
    if (isPrivileged) {
      if (instructorFilter === 'private_only') {
        return messages.filter((m) => m.isPrivate);
      }
      if (instructorFilter === 'students_only') {
        return messages.filter((m) => m.senderRole === 'student');
      }
      return messages;
    }

    // Student view: Stop students from seeing each other's messages
    return messages.filter((m) => {
      // 1. Their own message
      if (m.senderRole === 'student' && m.senderId === currentUser.id) {
        return true;
      }
      // 2. Instructor or Admin message
      if (m.senderRole === 'instructor' || m.senderRole === 'admin') {
        // If private reply, only show if this student is the intended recipient!
        if (m.isPrivate) {
          return m.recipientId === currentUser.id;
        }
        // Broadcast to all
        return true;
      }
      // Other students' messages are hidden
      return false;
    });
  }, [messages, isPrivileged, currentUser.id, instructorFilter]);

  // Unread badge + auto-scroll logic
  useEffect(() => {
    const newCount = visibleMessages.length;
    const prevCount = prevMessageCountRef.current;

    if (newCount > prevCount) {
      if (isAtBottomRef.current) {
        // Auto-scroll to bottom
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        setUnreadCount(0);
      } else {
        // User has scrolled up — increment unread badge
        setUnreadCount((n) => n + (newCount - prevCount));
      }
    }
    prevMessageCountRef.current = newCount;
  }, [visibleMessages]);

  // Cooldown countdown ticker
  useEffect(() => {
    if (cooldownLeft <= 0) return;
    const t = setTimeout(() => setCooldownLeft((n) => Math.max(0, n - 100)), 100);
    return () => clearTimeout(t);
  }, [cooldownLeft]);

  const showError = useCallback(
    (msg: string) => {
      setError(msg);
      if (errorTimer) clearTimeout(errorTimer);
      const t = setTimeout(() => setError(''), 3500);
      setErrorTimer(t);
    },
    [errorTimer]
  );

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const atBottom = scrollHeight - (scrollTop + clientHeight) < 80;
    isAtBottomRef.current = atBottom;
    setIsAtBottom(atBottom);
    if (atBottom) setUnreadCount(0);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setUnreadCount(0);
  };

  // Instructor initiates a 1-to-1 private reply to a specific student
  const handleInitiatePrivateReply = (msg: ClassChatMessage) => {
    setReplyTarget({
      studentId: msg.senderId,
      studentName: msg.senderName,
      messageId: msg.id,
      snippet: msg.text.length > 80 ? `${msg.text.slice(0, 80)}...` : msg.text,
    });
    // Focus textarea
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 50);
  };

  const handleCancelPrivateReply = () => {
    setReplyTarget(null);
  };

  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError('');

    const trimmed = inputText.trim();
    if (!trimmed) return;
    if (trimmed.length > 350) {
      showError('Message exceeds 350 character limit.');
      return;
    }

    const now = Date.now();
    const msSinceLast = now - lastSentTime;
    if (msSinceLast < SEND_DEBOUNCE_MS) {
      const remaining = SEND_DEBOUNCE_MS - msSinceLast;
      setCooldownLeft(remaining);
      showError(`Please wait before sending again.`);
      return;
    }

    setSending(true);
    try {
      const isPrivate = Boolean(isPrivileged && replyTarget);

      await sendClassChatMessage({
        senderName: currentUser.name,
        senderRole: currentUser.role,
        senderId: currentUser.id,
        badgeLabel: currentUser.badge || (currentUser.role === 'student' ? `ID: ${currentUser.id}` : 'Faculty'),
        text: trimmed,
        videoUrl: youtubeUrl,
        videoId: ytVideoId || undefined,
        isPrivate,
        recipientId: replyTarget ? replyTarget.studentId : undefined,
        recipientName: replyTarget ? replyTarget.studentName : undefined,
        replyToMessageId: replyTarget ? replyTarget.messageId : undefined,
        replyToText: replyTarget ? replyTarget.snippet : undefined,
      });

      setInputText('');
      setReplyTarget(null);
      setLastSentTime(Date.now());
      isAtBottomRef.current = true;
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch (err: any) {
      showError(err.message || 'Failed to send message. Try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!isInstructor) return;
    try {
      await deleteClassChatMessage(messageId, youtubeUrl);
    } catch (err) {
      console.error('Failed to delete message:', err);
    }
  };

  const handleClearChat = async () => {
    if (!isInstructor) return;
    try {
      await clearClassChat(youtubeUrl);
      setShowClearConfirm(false);
    } catch (err) {
      console.error('Failed to clear chat:', err);
    }
  };

  const formatTimestamp = (rawTs: any): string => {
    if (!rawTs) return 'Just now';
    try {
      const date = rawTs.toDate ? rawTs.toDate() : new Date(rawTs);
      if (isNaN(date.getTime())) return 'Just now';
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return 'Just now';
    }
  };

  // Theme classes
  const isDarkBlue = theme === 'dark-blue';
  const containerClasses = isDarkBlue
    ? 'bg-white/10 backdrop-blur-xl border border-white/20 text-white shadow-2xl'
    : 'bg-slate-900/90 backdrop-blur-xl border border-slate-800 text-slate-100 shadow-xl';
  const headerBorderClass = isDarkBlue ? 'border-white/15' : 'border-slate-800';
  const inputBgClasses = isDarkBlue
    ? 'bg-white/10 border-white/25 text-white placeholder-blue-200/60 focus:ring-yellow-400/70 focus:bg-white/15'
    : 'bg-slate-800/80 border-slate-700 text-white placeholder-slate-400 focus:ring-indigo-400 focus:bg-slate-800';

  const charsLeft = 350 - inputText.length;

  return (
    <div className={`relative rounded-2xl flex flex-col overflow-hidden transition-all ${maxHeightClass} ${containerClasses} ${className}`}>
      {/* ── Header ── */}
      <div className={`p-3 sm:p-3.5 border-b ${headerBorderClass} flex items-center justify-between gap-2 flex-shrink-0`}>
        <div className="flex items-center space-x-2.5 min-w-0">
          <div
            className={`w-8 h-8 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 ${
              isPrivileged
                ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30'
                : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
            }`}
          >
            {isPrivileged ? (
              <ChatBubbleLeftRightIcon className="w-4 h-4" />
            ) : (
              <LockClosedIcon className="w-4 h-4" />
            )}
          </div>
          <div className="truncate">
            <div className="flex items-center space-x-1.5 flex-wrap">
              <h3 className="font-bold text-xs sm:text-sm text-white tracking-tight">
                {isPrivileged ? 'Classroom Q&A & Global Chat' : 'Direct Instructor Channel'}
              </h3>
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              {viewerCount !== undefined && viewerCount > 0 && (
                <span
                  className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                    isDarkBlue ? 'bg-white/15 text-blue-100' : 'bg-slate-800 text-slate-300'
                  }`}
                >
                  👁 {viewerCount}
                </span>
              )}
            </div>
            <p
              className={`text-[10px] sm:text-[11px] truncate ${
                isDarkBlue ? 'text-blue-200/80' : 'text-slate-400'
              }`}
            >
              {isPrivileged
                ? 'Instructor Mode • Global Chat & 1-to-1 Private Replies'
                : '🔒 Private to instructor • Other candidates cannot see your questions'}
            </p>
          </div>
        </div>

        {/* Clear chat button for instructor */}
        {isInstructor && (
          <div className="flex items-center space-x-1 flex-shrink-0">
            {showClearConfirm ? (
              <div className="flex items-center space-x-1 bg-red-950/80 border border-red-500/50 p-1 rounded-lg">
                <span className="text-[10px] text-red-200 px-1">Clear all?</span>
                <button
                  type="button"
                  onClick={handleClearChat}
                  className="px-2 py-0.5 rounded bg-red-600 hover:bg-red-500 text-white text-[10px] font-bold"
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setShowClearConfirm(false)}
                  className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-200 text-[10px]"
                >
                  No
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowClearConfirm(true)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition"
                title="Clear Classroom Chat Messages"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Instructor Quick Filters (Global / 1-to-1 Private) ── */}
      {isPrivileged && (
        <div className={`px-3 py-1.5 bg-black/20 border-b ${headerBorderClass} flex items-center justify-between text-[11px]`}>
          <div className="flex items-center space-x-1.5 overflow-x-auto">
            <span className="text-slate-400 text-[10px] font-semibold flex items-center space-x-1">
              <FunnelIcon className="w-3 h-3" />
              <span>View:</span>
            </span>
            <button
              type="button"
              onClick={() => setInstructorFilter('all')}
              className={`px-2 py-0.5 rounded-lg text-[10px] font-bold transition ${
                instructorFilter === 'all'
                  ? 'bg-indigo-600 text-white shadow'
                  : 'bg-white/10 text-slate-300 hover:bg-white/20'
              }`}
            >
              All Messages ({messages.length})
            </button>
            <button
              type="button"
              onClick={() => setInstructorFilter('private_only')}
              className={`px-2 py-0.5 rounded-lg text-[10px] font-bold transition flex items-center space-x-1 ${
                instructorFilter === 'private_only'
                  ? 'bg-indigo-600 text-white shadow'
                  : 'bg-white/10 text-slate-300 hover:bg-white/20'
              }`}
            >
              <LockClosedIcon className="w-3 h-3 text-warning-400" />
              <span>1-to-1 Private Replies</span>
            </button>
            <button
              type="button"
              onClick={() => setInstructorFilter('students_only')}
              className={`px-2 py-0.5 rounded-lg text-[10px] font-bold transition ${
                instructorFilter === 'students_only'
                  ? 'bg-indigo-600 text-white shadow'
                  : 'bg-white/10 text-slate-300 hover:bg-white/20'
              }`}
            >
              Student Questions
            </button>
          </div>
        </div>
      )}

      {/* ── Mode Info Strip for Students ── */}
      {!isPrivileged && (
        <div className="px-3 py-1.5 bg-indigo-950/60 border-b border-indigo-500/20 flex items-center justify-between text-[10px] text-blue-200">
          <div className="flex items-center space-x-1.5">
            <ShieldCheckIcon className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
            <span>Private mode: Only you and the instructor can see your messages.</span>
          </div>
          <span className="font-mono text-emerald-300">ID: {currentUser.id}</span>
        </div>
      )}

      {/* ── Messages Feed ── */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3.5 scroll-smooth"
      >
        {visibleMessages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-6 space-y-2">
            <div
              className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-inner ${
                isDarkBlue ? 'bg-white/10 text-yellow-400' : 'bg-slate-800 text-indigo-400'
              }`}
            >
              <ChatBubbleLeftRightIcon className="w-6 h-6" />
            </div>
            <p className="text-xs sm:text-sm font-semibold text-white">
              {isPrivileged ? 'No messages in chat' : 'No messages yet'}
            </p>
            <p className={`text-[11px] max-w-xs ${isDarkBlue ? 'text-blue-200/80' : 'text-slate-400'}`}>
              {isPrivileged
                ? 'Student questions and your announcements will appear here in real time.'
                : 'Send your questions or doubts directly to the instructor. Other students cannot see what you write!'}
            </p>
          </div>
        ) : (
          visibleMessages.map((msg, index) => {
            const isMe = msg.senderId === currentUser.id;
            const isMsgInstructor = msg.senderRole === 'instructor' || msg.senderRole === 'admin';
            const isPrivateReply = Boolean(msg.isPrivate);
            const prevMsg = visibleMessages[index - 1];
            const sameAuthorAsPrev = prevMsg && prevMsg.senderId === msg.senderId && prevMsg.isPrivate === msg.isPrivate;

            return (
              <div
                key={msg.id || index}
                className={`group relative flex flex-col text-xs transition-all ${
                  isMe ? 'items-end' : 'items-start'
                }`}
              >
                {/* Author line */}
                {!sameAuthorAsPrev && (
                  <div className="flex items-center space-x-1.5 mb-1 px-1 text-[11px] flex-wrap">
                    {isMsgInstructor ? (
                      <span className="flex items-center space-x-1 font-bold text-emerald-400">
                        <ShieldCheckIcon className="w-3.5 h-3.5 inline" />
                        <span>{msg.senderName}</span>
                        <span className="px-1.5 rounded text-[9px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                          Instructor
                        </span>
                      </span>
                    ) : (
                      <span className="flex items-center space-x-1 font-semibold text-white/90">
                        <UserCircleIcon className="w-3.5 h-3.5 text-blue-300 inline" />
                        <span>{msg.senderName}</span>
                        {msg.badgeLabel && (
                          <span className="px-1.5 rounded text-[9px] font-semibold bg-white/15 text-blue-200 border border-white/20">
                            {msg.badgeLabel}
                          </span>
                        )}
                      </span>
                    )}

                    {/* Private reply tag indicator */}
                    {isPrivateReply ? (
                      <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center space-x-1">
                        <LockClosedIcon className="w-2.5 h-2.5" />
                        <span>
                          {isPrivileged
                            ? `1-to-1 to ${msg.recipientName || `ID ${msg.recipientId}`}`
                            : 'Private to You'}
                        </span>
                      </span>
                    ) : isMsgInstructor ? (
                      <span className="px-1.5 py-0.2 rounded text-[9px] font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/30 flex items-center space-x-1">
                        <MegaphoneIcon className="w-2.5 h-2.5" />
                        <span>Broadcast to All</span>
                      </span>
                    ) : !isPrivileged ? (
                      <span className="px-1.5 py-0.2 rounded text-[9px] font-medium bg-white/10 text-blue-200 flex items-center space-x-1">
                        <LockClosedIcon className="w-2.5 h-2.5" />
                        <span>Sent to Instructor</span>
                      </span>
                    ) : null}

                    <span className="text-[10px] opacity-50 text-slate-300">
                      {formatTimestamp(msg.createdAt)}
                    </span>
                  </div>
                )}

                {/* Message Bubble + Actions */}
                <div className="relative max-w-[92%] sm:max-w-[85%] flex items-end gap-1.5">
                  <div
                    className={`px-3 py-2 rounded-2xl text-xs sm:text-[13px] leading-relaxed break-words shadow-md whitespace-pre-wrap ${
                      isPrivateReply
                        ? 'bg-gradient-to-r from-amber-950/80 to-slate-900 border border-amber-500/40 text-amber-100 rounded-bl-sm ring-1 ring-amber-500/30'
                        : isMsgInstructor
                        ? 'bg-gradient-to-r from-emerald-700/90 to-teal-700/90 text-white font-medium border border-emerald-400/30 rounded-bl-sm'
                        : isMe
                        ? isDarkBlue
                          ? 'bg-yellow-500 text-slate-950 font-medium rounded-br-sm'
                          : 'bg-indigo-600 text-white font-medium rounded-br-sm'
                        : isDarkBlue
                        ? 'bg-white/15 text-white border border-white/15 rounded-bl-sm'
                        : 'bg-slate-800 text-slate-200 border border-slate-700/70 rounded-bl-sm'
                    }`}
                  >
                    {/* Quoted parent question if this was a 1-to-1 reply */}
                    {msg.replyToText && (
                      <div className="mb-2 p-1.5 rounded-lg bg-black/40 border-l-2 border-amber-400 text-[11px] text-amber-200/90 line-clamp-2 italic">
                        "{msg.replyToText}"
                      </div>
                    )}

                    {msg.text}
                  </div>

                  {/* Instructor "Reply Privately" Button on student messages */}
                  {isPrivileged && msg.senderRole === 'student' && (
                    <button
                      type="button"
                      onClick={() => handleInitiatePrivateReply(msg)}
                      className="opacity-0 group-hover:opacity-100 px-2 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-[10px] font-bold transition flex items-center space-x-1 shadow-md flex-shrink-0 cursor-pointer"
                      title={`Reply privately to ${msg.senderName} (1-to-1)`}
                    >
                      <LockClosedIcon className="w-3 h-3 text-warning-300" />
                      <span className="hidden sm:inline">Reply Privately</span>
                    </button>
                  )}

                  {/* Instructor delete on hover */}
                  {isInstructor && msg.id && (
                    <button
                      type="button"
                      onClick={() => handleDeleteMessage(msg.id!)}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/20 text-slate-400 hover:text-red-400 transition flex-shrink-0"
                      title="Delete message"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Timestamp on same-author continued messages */}
                {sameAuthorAsPrev && (
                  <span className="text-[9px] opacity-40 text-slate-400 px-1 mt-0.5">
                    {formatTimestamp(msg.createdAt)}
                  </span>
                )}
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* ── Scroll-to-bottom + unread badge ── */}
      {!isAtBottom && (
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20">
          <button
            type="button"
            onClick={scrollToBottom}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs font-semibold shadow-xl backdrop-blur-sm transition-all ${
              isDarkBlue
                ? 'bg-yellow-500 text-slate-950 hover:bg-yellow-400'
                : 'bg-indigo-600 text-white hover:bg-indigo-500'
            }`}
          >
            <ChevronDownIcon className="w-3.5 h-3.5" />
            {unreadCount > 0 ? (
              <span>{unreadCount} new message{unreadCount !== 1 ? 's' : ''}</span>
            ) : (
              <span>Scroll to bottom</span>
            )}
          </button>
        </div>
      )}

      {/* ── Input Bar ── */}
      <div className={`p-3 border-t ${headerBorderClass} flex-shrink-0 space-y-1.5 bg-black/20`}>
        {/* Active 1-to-1 Private Reply Target Banner */}
        {replyTarget && (
          <div className="p-2.5 rounded-xl bg-amber-950/80 border border-amber-500/40 text-amber-200 text-xs flex items-center justify-between gap-2 animate-in fade-in">
            <div className="flex items-center space-x-2 truncate">
              <LockClosedIcon className="w-4 h-4 text-warning-400 flex-shrink-0" />
              <div className="truncate">
                <span className="font-bold text-white">
                  Replying Privately 1-to-1 to {replyTarget.studentName}
                </span>{' '}
                <span className="font-mono text-[10px] text-amber-300">(ID: {replyTarget.studentId})</span>
                <span className="text-[11px] text-amber-200/80 ml-1.5 italic truncate">
                  "{replyTarget.snippet}"
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleCancelPrivateReply}
              className="text-amber-300 hover:text-white flex items-center space-x-0.5 text-[11px] font-semibold px-2 py-0.5 rounded bg-white/10 hover:bg-white/20 flex-shrink-0"
              title="Cancel Private Reply"
            >
              <XMarkIcon className="w-3.5 h-3.5" />
              <span>Cancel</span>
            </button>
          </div>
        )}

        {error && (
          <div className="flex items-center space-x-1.5 text-xs text-red-300 px-1 animate-pulse">
            <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
            {cooldownLeft > 0 && (
              <span className="font-mono text-red-400 ml-auto">{(cooldownLeft / 1000).toFixed(1)}s</span>
            )}
          </div>
        )}

        <form onSubmit={handleSendMessage} className="flex items-end space-x-2">
          <div className="relative flex-1">
            <textarea
              ref={textareaRef}
              rows={1}
              value={inputText}
              onChange={(e) => {
                setInputText(e.target.value);
                if (error) setError('');
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px';
              }}
              onKeyDown={handleKeyDown}
              maxLength={350}
              placeholder={
                replyTarget
                  ? `Type private 1-to-1 reply to ${replyTarget.studentName}...`
                  : isPrivileged
                  ? 'Send a broadcast message to all students...'
                  : 'Ask the instructor a question (Private 1-to-1)...'
              }
              disabled={sending}
              className={`w-full py-2.5 pl-3.5 pr-14 rounded-xl text-xs sm:text-sm border focus:outline-none focus:ring-2 transition-all shadow-inner resize-none leading-relaxed overflow-hidden ${inputBgClasses}`}
              style={{ minHeight: '40px', maxHeight: '96px' }}
            />
            {inputText.length > 250 && (
              <span className={`absolute right-3 bottom-2.5 text-[10px] font-mono ${charsLeft < 20 ? 'text-red-400' : 'text-slate-400'}`}>
                {charsLeft}
              </span>
            )}
          </div>

          <button
            type="submit"
            disabled={sending || !inputText.trim() || cooldownLeft > 0}
            className={`p-2.5 rounded-xl text-white font-bold flex items-center justify-center transition-all shadow-md active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0 cursor-pointer ${
              replyTarget
                ? 'bg-amber-600 hover:bg-amber-500 shadow-amber-600/30'
                : isPrivileged
                ? 'bg-emerald-600 hover:bg-emerald-500'
                : isDarkBlue
                ? 'bg-yellow-500 hover:bg-yellow-400 text-slate-950'
                : 'bg-indigo-600 hover:bg-indigo-500'
            }`}
            title={replyTarget ? `Send Private Reply to ${replyTarget.studentName}` : 'Send Message (Enter)'}
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <PaperAirplaneIcon className="w-4 h-4" />
            )}
          </button>
        </form>

        <div className="flex items-center justify-between text-[10px] text-slate-400 px-1">
          <span>
            {replyTarget ? (
              <span className="text-amber-300 font-semibold flex items-center space-x-1">
                <LockClosedIcon className="w-3 h-3 inline" />
                <span>1-to-1 Private Reply active (Enter to send)</span>
              </span>
            ) : isPrivileged ? (
              <span>Broadcasting to all students • Shift+Enter for new line</span>
            ) : (
              <span>Your message is sent privately to the instructor</span>
            )}
          </span>
          <span>{visibleMessages.length} message{visibleMessages.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
};

export default ClassChatBox;
